import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import PlateformService from '../components/PlateformService';
import Select from 'react-select';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
  Checkbox,
  InputAdornment,
  IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

function ListofProducts(props) {
    const PRODUCTS_BATCH_SIZE = 12;
    const [categoriesOptions, setCategoriesOption] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PRODUCTS_BATCH_SIZE);
    const [priceAdjustment, setPriceAdjustment] = useState('');
    const [adjustmentType, setAdjustmentType] = useState('percentage'); // 'percentage' or 'fixed'
    const [updating, setUpdating] = useState(false);
    const [updateProgress, setUpdateProgress] = useState(0);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'increase' or 'decrease'
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState({ src: '', name: '' });
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('table');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [editedPrices, setEditedPrices] = useState({});
    const [previewPrices, setPreviewPrices] = useState({});
    const [saleEdit, setSaleEdit] = useState({ productId: null, value: '' });
    const [saleConfirmOpen, setSaleConfirmOpen] = useState(false);
    const [salePending, setSalePending] = useState(null);
    const loadMoreRef = useRef(null);

        const [wooPlatform, setWooPlatform] = useState(null);

        const buildWooHeaders = (platform) => {
            if (platform && platform.login && platform.password) {
                return {
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + btoa(`${platform.login}:${platform.password}`),
                };
            }

            return {
                'Content-Type': 'application/json',
                'Authorization': 'Basic ' + btoa(`${process.env.REACT_APP_API_CLIENT_WEBSITE}:${process.env.REACT_APP_API_SECRET_WEBSITE}`)
            };
        };

        const fetchCategory = async () => {
                try {
                        // prefer platform credentials of type 'woocommerce'
                        let platform = wooPlatform;
                        if (!platform) {
                                const data = await PlateformService.fetchPlateforms();
                                const list = data?.response || data || [];
                                platform = (Array.isArray(list) ? list : []).find(p => (`${p?.type || ''}`).toLowerCase() === 'woocommerce');
                                if (platform) setWooPlatform(platform);
                        }

                        const baseUrl = platform?.additionnal_data?.url || process.env.REACT_APP_WEBSITE_API;
                        const targetUrl = `${baseUrl.replace(/\/+$/,'')}/wp-json/wc/v3/products/categories?per_page=100`;
                        const hdrs = buildWooHeaders(platform);

                        const response = await axios.get(targetUrl, { headers: hdrs });
                        const categories = (response.data || []).map(x => ({ value: x.id, label: x.name }));
                        setCategoriesOption(categories);
                } catch (error) {
                        console.error('Error loading categories via WooCommerce platform:', error);
                        setErrorMessage('Error loading categories');
                }
        };

    const fetchProductsByCategory = async (categoryId) => {
        setLoading(true);
        setProducts([]);
        setErrorMessage('');
        
        try {
            // prefer platform credentials
            let platform = wooPlatform;
            if (!platform) {
                const data = await PlateformService.fetchPlateforms();
                const list = data?.response || data || [];
                platform = (Array.isArray(list) ? list : []).find(p => (`${p?.type || ''}`).toLowerCase() === 'woocommerce');
                if (platform) setWooPlatform(platform);
            }

            const baseUrl = platform?.additionnal_data?.url || process.env.REACT_APP_WEBSITE_API;
            const targetUrl = `${baseUrl.replace(/\/+$/,'')}/wp-json/wc/v3/products?category=${categoryId}&status=publish&per_page=100`;
            const hdrs = buildWooHeaders(platform);
            const response = await axios.get(targetUrl, { headers: hdrs });
            setProducts(response.data);
            setVisibleCount(PRODUCTS_BATCH_SIZE);
        } catch (error) {
            console.error('Error:', error);
            setErrorMessage('Error loading products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategory();
    }, []);

    const onChangeCategory = (newValue) => {
        setSelectedCategories(newValue);
        if (newValue) {
            fetchProductsByCategory(newValue.value);
        } else {
            setProducts([]);
        }
    };

    const calculateNewPrice = (currentPrice, isIncrease) => {
        const adjustment = parseFloat(priceAdjustment);
        if (isNaN(adjustment) || adjustment <= 0) return currentPrice;

        const current = parseFloat(currentPrice);
        if (adjustmentType === 'percentage') {
            const changeAmount = (current * adjustment) / 100;
            return isIncrease ? current + changeAmount : current - changeAmount;
        } else {
            return isIncrease ? current + adjustment : current - adjustment;
        }
    };

    const updateProductPrice = async (productId, newRegularPrice, newSalePrice) => {
        try {
            // Prefer platform URL if available
            let platform = wooPlatform;
            if (!platform) {
                const data = await PlateformService.fetchPlateforms();
                const list = data?.response || data || [];
                platform = (Array.isArray(list) ? list : []).find(p => (`${p?.type || ''}`).toLowerCase() === 'woocommerce');
                if (platform) setWooPlatform(platform);
            }
            const baseUrl = platform?.additionnal_data?.url || process.env.REACT_APP_WEBSITE_API;
            const targetUrl = `${baseUrl.replace(/\/+$/,'')}/wp-json/wc/v3/products/${productId}`;
            const updateData = {
                regular_price: newRegularPrice.toFixed(2).toString()
            };
            
            // Only update sale price if it exists
            if (newSalePrice !== null) {
                updateData.sale_price = newSalePrice.toFixed(2).toString();
            }
            
            const hdrs = buildWooHeaders(platform);
            await axios.put(targetUrl, updateData, { headers: hdrs });
            return { success: true, productId };
        } catch (error) {
            console.error(`Error updating product ${productId}:`, error);
            return { success: false, productId, error };
        }
    };

    const handlePriceChangeRequest = (isIncrease) => {
        if (selectedProducts.length === 0) {
            setErrorMessage('Please select at least one product');
            return;
        }
        
        if (!priceAdjustment || parseFloat(priceAdjustment) <= 0) {
            setErrorMessage('Please enter a valid price adjustment value');
            return;
        }
        
        setEditedPrices({}); // Reset edited prices
        setPendingAction(isIncrease ? 'increase' : 'decrease');
        setConfirmDialogOpen(true);
        // compute preview prices for selected products so table shows changes
        const productsToPreview = products.filter(p => selectedProducts.includes(p.id));
        const preview = {};
        productsToPreview.forEach(product => {
            const currentRegularPrice = parseFloat(product.regular_price) || 0;
            const calculatedRegularPrice = calculateNewPrice(currentRegularPrice, isIncrease);
            const defaultNewRegularPrice = Math.floor(calculatedRegularPrice);

            let floorSalePrice = null;
            if (product.sale_price) {
                const currentSalePrice = parseFloat(product.sale_price) || 0;
                const calculatedSalePrice = calculateNewPrice(currentSalePrice, isIncrease);
                floorSalePrice = Math.floor(calculatedSalePrice);
            }

            preview[product.id] = {
                regular: defaultNewRegularPrice,
                sale: floorSalePrice
            };
        });
        setPreviewPrices(preview);
    };

    const handleConfirmPriceChange = () => {
        setConfirmDialogOpen(false);
        processPriceQueue(pendingAction === 'increase');
    };

    const handleCancelPriceChange = () => {
        setConfirmDialogOpen(false);
        setPendingAction(null);
        setPreviewPrices({});
    };

    const handleImageClick = (imageSrc, productName) => {
        setSelectedImage({ src: imageSrc, name: productName });
        setImageModalOpen(true);
    };

    const handleCloseImageModal = () => {
        setImageModalOpen(false);
    };

    const handleSelectProduct = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedProducts(filteredProducts.map(p => p.id));
        } else {
            setSelectedProducts([]);
        }
    };

    const handleEditPrice = (productId, field, value) => {
        setEditedPrices(prev => ({
            ...prev,
            [productId]: {
                ...prev[productId],
                [field]: value
            }
        }));
    };

    const filteredProducts = products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        setVisibleCount(PRODUCTS_BATCH_SIZE);
    }, [searchQuery, selectedCategories]);

    useEffect(() => {
        const node = loadMoreRef.current;
        if (!node) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry?.isIntersecting) {
                    setVisibleCount((prev) => (
                        prev >= filteredProducts.length ? prev : Math.min(prev + PRODUCTS_BATCH_SIZE, filteredProducts.length)
                    ));
                }
            },
            {
                root: null,
                rootMargin: '200px 0px',
                threshold: 0.1,
            }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [filteredProducts.length]);

    const updateProductStock = async (productId, inStock) => {
        try {
            let platform = wooPlatform;
            if (!platform) {
                const data = await PlateformService.fetchPlateforms();
                const list = data?.response || data || [];
                platform = (Array.isArray(list) ? list : []).find(p => (`${p?.type || ''}`).toLowerCase() === 'woocommerce');
                if (platform) setWooPlatform(platform);
            }
            const baseUrl = platform?.additionnal_data?.url || process.env.REACT_APP_WEBSITE_API;
            const targetUrl = `${baseUrl.replace(/\/+$/,'')}/wp-json/wc/v3/products/${productId}`;
            const hdrs = buildWooHeaders(platform);
            await axios.put(targetUrl, {
                stock_status: inStock ? 'instock' : 'outofstock'
            }, { headers: hdrs });
            return { success: true, productId };
        } catch (error) {
            console.error(`Error updating stock for product ${productId}:`, error);
            return { success: false, productId, error };
        }
    };

    const handleStockToggle = async (productId, currentStatus) => {
        const newStatus = currentStatus === 'instock' ? 'outofstock' : 'instock';
        const result = await updateProductStock(productId, newStatus === 'instock');
        
        if (result.success) {
            setProducts(prevProducts => 
                prevProducts.map(p => 
                    p.id === productId 
                        ? { ...p, stock_status: newStatus }
                        : p
                )
            );
        } else {
            setErrorMessage('Failed to update stock status');
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };

    const bulkUpdateStock = async (setInStock) => {
        setUpdating(true);
        setUpdateProgress(0);
        setSuccessMessage('');
        setErrorMessage('');

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < products.length; i++) {
            const product = products[i];
            const result = await updateProductStock(product.id, setInStock);
            
            if (result.success) {
                successCount++;
                setProducts(prevProducts => 
                    prevProducts.map(p => 
                        p.id === product.id 
                            ? { ...p, stock_status: setInStock ? 'instock' : 'outofstock' }
                            : p
                    )
                );
            } else {
                failCount++;
            }

            setUpdateProgress(((i + 1) / products.length) * 100);
        }

        setUpdating(false);
        setSuccessMessage(`Updated stock for ${successCount} products${failCount > 0 ? `, ${failCount} failed` : ''}`);
        
        if (selectedCategories) {
            setTimeout(() => fetchProductsByCategory(selectedCategories.value), 1000);
        }
    };

    const processPriceQueue = async (isIncrease) => {
        setUpdating(true);
        setUpdateProgress(0);
        setSuccessMessage('');
        setErrorMessage('');

        const updates = [];
        let successCount = 0;
        let failCount = 0;

        const productsToUpdate = products.filter(p => selectedProducts.includes(p.id));

        for (let i = 0; i < productsToUpdate.length; i++) {
            const product = productsToUpdate[i];
            
            // Check if price was manually edited
            let newRegularPrice;
            let newSalePrice = null;
            
            if (editedPrices[product.id]?.regular_price !== undefined) {
                // Use manually edited price
                newRegularPrice = parseFloat(editedPrices[product.id].regular_price);
                
                // If regular price was edited, delete sale price or update it
                if (product.sale_price) {
                    const currentSalePrice = parseFloat(product.sale_price);
                    // Keep sale price if it's still less than new regular price, otherwise delete it
                    if (currentSalePrice < newRegularPrice) {
                        newSalePrice = currentSalePrice;
                    } else {
                        newSalePrice = null; // Will be handled as empty string in API
                    }
                }
            } else {
                // Calculate based on adjustment
                const currentRegularPrice = parseFloat(product.regular_price) || 0;
                const calculatedRegularPrice = calculateNewPrice(currentRegularPrice, isIncrease);
                newRegularPrice = Math.floor(calculatedRegularPrice);

                // Calculate new sale price if it exists
                if (product.sale_price) {
                    const currentSalePrice = parseFloat(product.sale_price) || 0;
                    const calculatedSalePrice = calculateNewPrice(currentSalePrice, isIncrease);
                    newSalePrice = Math.floor(calculatedSalePrice);
                }
            }

            if (newRegularPrice >= 0) {
                const result = await updateProductPrice(product.id, newRegularPrice, newSalePrice);
                if (result.success) {
                    successCount++;
                    // Update local state
                    setProducts(prevProducts => 
                        prevProducts.map(p => 
                            p.id === product.id 
                                ? { 
                                    ...p, 
                                    regular_price: newRegularPrice.toFixed(2),
                                    sale_price: newSalePrice ? newSalePrice.toFixed(2) : p.sale_price
                                  }
                                : p
                        )
                    );
                } else {
                    failCount++;
                }
            }

            setUpdateProgress(((i + 1) / productsToUpdate.length) * 100);
        }

        setUpdating(false);
        setSuccessMessage(`Updated ${successCount} products successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
        setSelectedProducts([]); // Clear selection
        setEditedPrices({}); // Clear edited prices
        setPreviewPrices({});
        
        // Refresh products from server
        if (selectedCategories) {
            setTimeout(() => fetchProductsByCategory(selectedCategories.value), 1000);
        }
    };

    const visibleProducts = filteredProducts.slice(0, visibleCount);
    const hasMoreProducts = visibleCount < filteredProducts.length;

    return (
        <div className="container-fluid mt-3">
            <Typography variant="h4" gutterBottom>
                Product List Manager
            </Typography>
            
            <Box sx={{ mb: 3 }}>
                <label htmlFor="categories" className="form-label">Catégories</label>
                <Select
                    id="categories"
                    options={categoriesOptions}
                    value={selectedCategories}
                    onChange={onChangeCategory}
                    placeholder="Select a category..."
                />
            </Box>

            {products.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search products by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {selectedProducts.length} of {filteredProducts.length} products selected
                    </Typography>
                </Box>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {products.length > 0 && (
                <>
                    <Paper sx={{ mb: 3, p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Product display
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Keep the table view or switch to cards for larger images and easier stock handling.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant={viewMode === 'table' ? 'contained' : 'outlined'}
                                    onClick={() => setViewMode('table')}
                                >
                                    Table
                                </Button>
                                <Button
                                    variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                                    onClick={() => setViewMode('cards')}
                                >
                                    Cards
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper sx={{ mb: 3, p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Bulk Price Adjustment {selectedProducts.length > 0 && `(${selectedProducts.length} products selected)`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                label="Adjustment Value"
                                type="number"
                                value={priceAdjustment}
                                onChange={(e) => setPriceAdjustment(e.target.value)}
                                size="small"
                                sx={{ width: 150 }}
                            />
                            <Select
                                options={[
                                    { value: 'percentage', label: 'Percentage (%)' },
                                    { value: 'fixed', label: 'Fixed Amount (MAD)' }
                                ]}
                                value={{ value: adjustmentType, label: adjustmentType === 'percentage' ? 'Percentage (%)' : 'Fixed Amount (MAD)' }}
                                onChange={(option) => setAdjustmentType(option.value)}
                                styles={{ container: (base) => ({ ...base, width: 200 }) }}
                            />
                            <Button 
                                variant="contained" 
                                color="success"
                                onClick={() => handlePriceChangeRequest(true)}
                                disabled={updating || !priceAdjustment}
                            >
                                Increase All Prices
                            </Button>
                            <Button 
                                variant="contained" 
                                color="error"
                                onClick={() => handlePriceChangeRequest(false)}
                                disabled={updating || !priceAdjustment}
                            >
                                Decrease All Prices
                            </Button>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle2">
                                Bulk Stock Control:
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={() => bulkUpdateStock(true)}
                                disabled={updating}
                            >
                                Set All In Stock
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="warning"
                                onClick={() => bulkUpdateStock(false)}
                                disabled={updating}
                            >
                                Set All Out of Stock
                            </Button>
                        </Box>
                        
                        {updating && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    Updating products... {Math.round(updateProgress)}%
                                </Typography>
                                <LinearProgress variant="determinate" value={updateProgress} />
                            </Box>
                        )}
                        
                        {successMessage && (
                            <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>
                        )}
                    </Paper>

                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
                    )}

                    {viewMode === 'table' ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
                                                checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>Image</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Regular Price (MAD)</TableCell>
                                        <TableCell align="right">Sale Price (MAD)</TableCell>
                                        <TableCell>SKU</TableCell>
                                        <TableCell align="center">Stock Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleProducts.map((product) => {
                                        const imageSrc = product.images && product.images[0] ? product.images[0].src : '/placeholder.png';
                                        return (
                                            <TableRow key={product.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedProducts.includes(product.id)}
                                                        onChange={() => handleSelectProduct(product.id)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <img
                                                        src={imageSrc}
                                                        alt={product.name}
                                                        style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                                                        onClick={() => handleImageClick(imageSrc, product.name)}
                                                    />
                                                </TableCell>
                                                <TableCell>{product.name}</TableCell>
                                                <TableCell align="right">
                                                    <strong>{parseFloat(product.regular_price).toFixed(2)}</strong>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {saleEdit.productId === product.id ? (
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={saleEdit.value}
                                                                onChange={(e) => setSaleEdit(prev => ({ ...prev, value: e.target.value }))}
                                                                sx={{ width: 120 }}
                                                            />
                                                            <Button size="small" variant="contained" onClick={() => { setSalePending({ product, value: saleEdit.value }); setSaleConfirmOpen(true); }}>
                                                                Save
                                                            </Button>
                                                            <Button size="small" variant="outlined" onClick={() => setSaleEdit({ productId: null, value: '' })}>
                                                                Cancel
                                                            </Button>
                                                        </Box>
                                                    ) : (
                                                        <Box onDoubleClick={() => {
                                                            setSaleEdit({ productId: product.id, value: product.sale_price || '' });
                                                        }} sx={{ cursor: 'pointer', textAlign: 'right' }}>
                                                            {previewPrices[product.id] && previewPrices[product.id].sale ? (
                                                                <Typography variant="body2" color="primary">{previewPrices[product.id].sale.toFixed ? previewPrices[product.id].sale.toFixed(2) : `${previewPrices[product.id].sale}`} MAD</Typography>
                                                            ) : (
                                                                product.sale_price ? `${parseFloat(product.sale_price).toFixed(2)} MAD` : '-'
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>{product.sku || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                        <Switch
                                                            checked={product.stock_status === 'instock'}
                                                            onChange={() => handleStockToggle(product.id, product.stock_status)}
                                                            color="success"
                                                            size="small"
                                                        />
                                                        <Chip
                                                            label={product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                                                            color={product.stock_status === 'instock' ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        lg: 'repeat(4, minmax(0, 1fr))',
                                    },
                                    gap: 2,
                                }}
                            >
                                {visibleProducts.map((product) => {
                                    const imageSrc = product.images && product.images[0] ? product.images[0].src : '/placeholder.png';
                                    const isSelected = selectedProducts.includes(product.id);
                                    const regularPrice = parseFloat(product.regular_price) || 0;
                                    const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;

                                    return (
                                        <Paper
                                            key={product.id}
                                            elevation={0}
                                            onClick={() => handleSelectProduct(product.id)}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                cursor: 'pointer',
                                                border: isSelected ? '2px solid' : '1px solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                background: isSelected
                                                    ? 'linear-gradient(180deg, #dbeafe 0%, #eff6ff 100%)'
                                                    : 'background.paper',
                                                boxShadow: isSelected
                                                    ? '0 18px 40px rgba(37, 99, 235, 0.20)'
                                                    : '0 8px 24px rgba(15, 23, 42, 0.06)',
                                                color: 'text.primary',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <Box sx={{ position: 'relative', mb: 2 }}>
                                                <Box
                                                    component="img"
                                                    src={imageSrc}
                                                    alt={product.name}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleImageClick(imageSrc, product.name);
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        height: 220,
                                                        objectFit: 'contain',
                                                        borderRadius: 2,
                                                        backgroundColor: '#f8fafc',
                                                        cursor: 'zoom-in',
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    variant={isSelected ? 'contained' : 'outlined'}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleSelectProduct(product.id);
                                                    }}
                                                    sx={{ position: 'absolute', top: 12, right: 12, minWidth: 'auto' }}
                                                >
                                                    {isSelected ? 'Selected' : 'Select'}
                                                </Button>
                                            </Box>

                                            <Typography variant="h6" sx={{ mb: 1.5, minHeight: 56 }}>
                                                {product.name}
                                            </Typography>

                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                <Chip label={`Regular: ${regularPrice.toFixed(2)} MAD`} color="primary" size="small" />
                                                <Chip label={salePrice ? `Sale: ${salePrice.toFixed(2)} MAD` : 'No sale price'} color={salePrice ? 'success' : 'default'} size="small" />
                                                <Chip label={product.sku || 'No SKU'} variant="outlined" size="small" />
                                            </Box>

                                            <Box sx={{ display: 'grid', gap: 1.25 }}>
                                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: product.stock_status === 'instock' ? 'success.light' : 'grey.200' }}>
                                                    <Typography variant="caption" color="text.secondary">WooCommerce stock_status</Typography>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                                                    </Typography>
                                                </Box>

                                                <FormControlLabel
                                                    onClick={(event) => event.stopPropagation()}
                                                    control={
                                                        <Switch
                                                            checked={product.stock_status === 'instock'}
                                                            onChange={() => handleStockToggle(product.id, product.stock_status)}
                                                            color="success"
                                                        />
                                                    }
                                                    label={product.stock_status === 'instock' ? 'Turn stock off' : 'Turn stock on'}
                                                    sx={{ m: 0 }}
                                                />
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>

                        </>
                    )}

                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {visibleProducts.length} of {filteredProducts.length} products
                        </Typography>
                        {hasMoreProducts && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Scroll down to load more products
                            </Typography>
                        )}
                        <Box ref={loadMoreRef} sx={{ height: 1 }} />
                    </Box>
                </>
            )}

            {!loading && products.length === 0 && selectedCategories && (
                <Alert severity="info">
                    No products found in this category.
                </Alert>
            )}

            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelPriceChange}
                aria-labelledby="confirm-dialog-title"
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirm Price {pendingAction === 'increase' ? 'Increase' : 'Decrease'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to <strong>{pendingAction === 'increase' ? 'increase' : 'decrease'}</strong> the regular price of <strong>{selectedProducts.length} product(s)</strong> by:
                    </DialogContentText>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="h6" color={pendingAction === 'increase' ? 'success.main' : 'error.main'}>
                            {pendingAction === 'increase' ? '+' : '-'} {priceAdjustment} {adjustmentType === 'percentage' ? '%' : 'MAD'}
                        </Typography>
                    </Box>
                    
                    {/* Price Preview */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Price Estimations (Floor Values):
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Image</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="right">Current Regular</TableCell>
                                        <TableCell align="right">New Regular</TableCell>
                                        <TableCell align="right">Current Sale</TableCell>
                                        <TableCell align="right">New Sale</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.filter(p => selectedProducts.includes(p.id)).map((product) => {
                                        const currentRegularPrice = parseFloat(product.regular_price) || 0;
                                        const calculatedRegularPrice = calculateNewPrice(currentRegularPrice, pendingAction === 'increase');
                                        const defaultNewRegularPrice = Math.floor(calculatedRegularPrice);
                                        
                                        const displayRegularPrice = editedPrices[product.id]?.regular_price !== undefined 
                                            ? editedPrices[product.id].regular_price 
                                            : defaultNewRegularPrice;
                                        
                                        const currentSalePrice = product.sale_price ? parseFloat(product.sale_price) : null;
                                        const newSalePrice = currentSalePrice ? calculateNewPrice(currentSalePrice, pendingAction === 'increase') : null;
                                        const floorSalePrice = newSalePrice ? Math.floor(newSalePrice) : null;
                                        
                                        const imageSrc = product.images && product.images[0] ? product.images[0].src : '/placeholder.png';
                                        
                                        return (
                                            <TableRow key={product.id}>
                                                <TableCell>
                                                    <img 
                                                        src={imageSrc}
                                                        alt={product.name}
                                                        style={{ 
                                                            width: 50, 
                                                            height: 50, 
                                                            objectFit: 'cover', 
                                                            borderRadius: 4,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handleImageClick(imageSrc, product.name)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {product.name}
                                                </TableCell>
                                                <TableCell align="right">{currentRegularPrice.toFixed(2)} MAD</TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={displayRegularPrice}
                                                        onChange={(e) => handleEditPrice(product.id, 'regular_price', e.target.value)}
                                                        sx={{ width: 140 }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">MAD</InputAdornment>
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {currentSalePrice ? `${currentSalePrice.toFixed(2)} MAD` : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {floorSalePrice ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {floorSalePrice} MAD
                                                            {parseFloat(displayRegularPrice) <= floorSalePrice && (
                                                                <Typography variant="caption" color="error" display="block">
                                                                    (Will be removed)
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Total: {selectedProducts.length} product(s) will be updated. You can edit the "New Regular" price manually.
                        </Typography>
                    </Box>

                    <DialogContentText sx={{ mt: 2 }}>
                        This action will update all products in the selected category. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPriceChange} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmPriceChange} 
                        variant="contained"
                        color={pendingAction === 'increase' ? 'success' : 'error'}
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Modal */}
            <Dialog
                open={imageModalOpen}
                onClose={handleCloseImageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedImage.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <img 
                            src={selectedImage.src} 
                            alt={selectedImage.name}
                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageModal}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Sale edit confirm dialog */}
            <Dialog open={saleConfirmOpen} onClose={() => { setSaleConfirmOpen(false); setSalePending(null); }}>
                <DialogTitle>Confirm sale price change</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to set the sale price for <strong>{salePending?.product?.name}</strong> to <strong>{salePending?.value}</strong> MAD?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setSaleConfirmOpen(false); setSalePending(null); }}>Cancel</Button>
                    <Button variant="contained" onClick={async () => {
                        if (!salePending) return;
                        const pid = salePending.product.id;
                        const newSale = parseFloat(salePending.value) || 0;
                        const currentRegular = parseFloat(salePending.product.regular_price) || 0;
                        setSaleConfirmOpen(false);
                        try {
                            const res = await updateProductPrice(pid, currentRegular, newSale);
                            if (res.success) {
                                setProducts(prev => prev.map(p => p.id === pid ? { ...p, sale_price: newSale.toFixed(2) } : p));
                                setSaleEdit({ productId: null, value: '' });
                                setSalePending(null);
                            } else {
                                setErrorMessage('Failed to update sale price');
                                setTimeout(() => setErrorMessage(''), 3000);
                            }
                        } catch (e) {
                            console.error('Sale update error', e);
                            setErrorMessage('Failed to update sale price');
                            setTimeout(() => setErrorMessage(''), 3000);
                        }
                    }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ListofProducts;
